import { useEffect, useState } from 'react';
import firebaseService from '../../../firebase/firebaseService';

export type ClockData = {
  idf?: string;
  version?: number;
  connected?: boolean;
  collectionIds?: string[];
};

type ListenerState = {
  clockId: string | null;
  data: ClockData;
  // True once the machines doc reported. Only that document carries `version`,
  // so a machinesReport snapshot alone does not mean the data has loaded.
  hasMachineData: boolean;
};

const EMPTY_DATA: ClockData = {};

const useClockDataListener = (clockId: string | null) => {
  // State is tagged with the clock it belongs to, so `isLoading` and `data` are
  // both derived during render. Setting them from the effect instead would leave
  // the previous clock's values in place for the render in which clockId
  // changes, and a caller acting on that render would read a missing version as
  // "this clock is too old".
  const [state, setState] = useState<ListenerState>({
    clockId: null,
    data: EMPTY_DATA,
    hasMachineData: false,
  });

  useEffect(() => {
    if (!clockId) {
      return;
    }

    const unsubscribe = firebaseService.listenToClockData(
      clockId,
      ({ source, ...data }) => {
        setState((prev) => {
          const isSameClock = prev.clockId === clockId;
          return {
            clockId,
            // Never merge onto a previous clock's data, so its name or version
            // cannot leak into the next clock.
            data: isSameClock ? { ...prev.data, ...data } : data,
            hasMachineData:
              source === 'machines' || (isSameClock && prev.hasMachineData),
          };
        });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [clockId]);

  const isCurrentClock = state.clockId === clockId;

  return {
    data: isCurrentClock ? state.data : EMPTY_DATA,
    isLoading: !!clockId && !(isCurrentClock && state.hasMachineData),
  };
};

export default useClockDataListener;
